import React, {memo} from 'react';
import {useParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {getSpecificCustomer, selectCustomerById} from 'store/customers/customersSlice';
import {useCreateCustomerDocument, useGetSpecificCustomerDocuments} from 'hooks/customer/documents.hooks';

// material-ui
import {useTheme} from '@mui/material/styles';
import {Box, Button, Dialog, Grid, IconButton, TextField, Tooltip, Typography, Checkbox, FormControlLabel} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import MainCard from 'ui-component/cards/MainCard';

// locale
import {FormattedMessage} from 'react-intl';

// icons
import EventIcon from '@mui/icons-material/Event';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';

// project imports
import CustomerProductsList from 'views/customer/profile/products/CustomerProductsList';
import DocumentCustomerDetails from '../DocumentCustomerDetails';
import CalendarContainer from 'ui-component/calendar-container/calendar-container';

// types
import {createDocument, needsTypedFirstUnit, TYPED_FIRST_UNIT_MESSAGE} from 'types/DocumentsTypes/utils';
import IItem, {applyDualUnitAmounts} from 'types/product_delivery';

import Swal from 'sweetalert2';
import {collect, getClientsDaily} from '../../../../../api/customer/documents.api';
import getCustomerProductsPrices, {getSupplierProductsPrices} from '../../../../../api/customer/products_prices.api';
import {dateObjectToString} from '../../../../../fromKotlin/nkutils';
import JSDB, {NKTypeConvertor} from '../../../../../fromKotlin/nk';
import {DocumentType, ProductNote, ExtendedDocument} from '../../../../../types/DocumentsTypes/Document';
import {Details} from '../../../../../types/DocumentsTypes/utils';
import DocumentPreview from '../DocumentPreview';
import NoNegative from '../../../../../ui-component/textFieldNumber';
import {selectDrivers} from "../../../../../store/agents/agentsSlice";
import Agent from "../../../../../types/agent";
import {applyTaklitPricesToItems, isTaklitEnabled} from '../../../../../api/customer/taklitPrices';
import {isVariableConversionRate} from '../../../../../utils/cofcFeatures';

const NewDeliveryCertificate = ({
                                    upid,
                                    dtype,
                                    narrow,
                                    productsFromAbove,
                                    refId, fromOrder, initialDocumentDate, markFromOrder
                                }: { upid: string, dtype: DocumentType, narrow?: boolean, productsFromAbove?: IItem[], refId?: number[], fromOrder?: any, initialDocumentDate?: Date, markFromOrder?: boolean }) => {
    const theme = useTheme();
    const {id} = useParams();
    const dispatch = useDispatch();

    const documentType: DocumentType = dtype ? dtype : location.pathname.endsWith('DeliveryCertificate') ? 'delivery_certificate' : 'order';
    // TODO: Change to single customer fetching
    const customer = useSelector((state: any) => selectCustomerById(state, id ?? upid));
    React.useEffect(() => {
        const cid = id ?? upid;
        if (!customer && cid) dispatch(getSpecificCustomer(Number(cid)));
    }, [dispatch, customer, id, upid]);
    const [date, setDate] = React.useState<Date>(initialDocumentDate ?? fromOrder ?? new Date());
    const [products, setProducts] = React.useState<IItem[]>([]);
    const [bul, setBul] = React.useState<number>();
    const [autoNum, setAutoNum] = React.useState<number | undefined>(undefined);
    const drivers = useSelector(selectDrivers);
    const [productLoaded,setProductLoaded] = React.useState<boolean>(false);
    const [driver, setDriver] = React.useState<Agent | undefined>(undefined);
    const [mainValue, setMainValue] = React.useState<number>(0);
    const [tax, setTaxValue] = React.useState<number>(0);
    const [getAllProducts, setGetAllProducts] = React.useState<boolean>(false);
    const [withDiscount] = React.useState<boolean>(parseInt(localStorage.getItem('withDiscount') ?? '0') == 1);
    const [savePrice,setSavePrice] = React.useState<boolean | undefined>(undefined);
    const [details, setDetails] = React.useState<Details>({
        city: '', companyId: '', companyName: '', email: '', id: 0, phone: '', street: '', streetNumber: ''
    });
    // Forced document fields (static_data): override the client's address/business on the printed note.
    const [forceStatic, setForceStatic] = React.useState<boolean>(false);
    const [openForce, setOpenForce] = React.useState<boolean>(false);
    const [forcedDetails, setForcedDetails] = React.useState<Details>({
        city: '', companyId: '', companyName: '', email: '', id: 0, phone: '', street: '', streetNumber: ''
    });
    const [commentControl, setCommentControl] = React.useState<any>({
        notes: '',
        notes2: '',
        notes3: '',
        notes4: '',
        notes5: '',
    });

    // Initialize details from customer
    React.useEffect(() => {
        if (customer) {
            const base = {
                city: customer.city,
                companyId: customer.businessId,
                companyName: customer.businessName,
                email: customer.email,
                id: Number(customer.id),
                phone: customer.phone,
                street: customer.street,
                streetNumber: customer.streetNumber
            };
            setDetails(base);
            setForcedDetails((prev) => (forceStatic ? prev : base));
        }
    }, [customer]);

    // State for duplicate delivery note calendar
    const [openDuplicateCalendar, setOpenDuplicateCalendar] = React.useState<boolean>(false);
    const [previewOpen, setPreviewOpen] = React.useState<boolean>(false);
    const [previewData, setPreviewData] = React.useState<any>({});
    const [calendarRange, setCalendarRange] = React.useState<[Date, Date] | null>(null);
    const [deliveryNotesForDuplicate, setDeliveryNotesForDuplicate] = React.useState<ExtendedDocument[]>([]);
    const [isLoadingDeliveryNotes, setIsLoadingDeliveryNotes] = React.useState<boolean>(false);

    // Fetch delivery notes for duplication (manual - only when needed)
    const {
        data: fetchedDeliveryNotes,
        isLoading: isFetchingNotes,
        refetch: refetchDeliveryNotes
    } = useGetSpecificCustomerDocuments(
        [documentType === 'delivery_certificate' ? 'delivery_certificate' : 'order'],
        Number(id ?? upid),
        calendarRange?.[0],
        calendarRange?.[1],
        undefined,
        undefined,
        true,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        {
            enabled: false // Disabled by default, only fetch when refetch is called
        }
    );

    // Update delivery notes when fetched
    React.useEffect(() => {
        if (fetchedDeliveryNotes) {
            setDeliveryNotesForDuplicate(fetchedDeliveryNotes);
            setIsLoadingDeliveryNotes(false);
        }
    }, [fetchedDeliveryNotes]);

    React.useEffect(() => {
        setIsLoadingDeliveryNotes(isFetchingNotes);
    }, [isFetchingNotes]);

    // Fetch notes when calendar range changes (user navigates calendar)
    React.useEffect(() => {
        if (calendarRange && openDuplicateCalendar) {
            setIsLoadingDeliveryNotes(true);
            refetchDeliveryNotes();
        }
    }, [calendarRange, openDuplicateCalendar]);

    React.useEffect(() => {
        if (productsFromAbove) {
            if (!isTaklitEnabled()) {
                setProducts(productsFromAbove);
                return;
            }
            let cancelled = false;
            (async () => {
                const {items} = await applyTaklitPricesToItems(
                    productsFromAbove,
                    dateObjectToString(date),
                    {onlyUntouched: true},
                );
                if (!cancelled) setProducts(items);
            })();
            return () => { cancelled = true; };
        }
    }, [productsFromAbove]);

    React.useEffect(() => {
        if (productsFromAbove) return;
        const genProds = async () => {
            const isSupplierNote = documentType === 'supplier_note';
            const ps = isSupplierNote
                ? await getSupplierProductsPrices(id ?? upid, dateObjectToString(date), getAllProducts)
                : await getCustomerProductsPrices(id ?? upid, dateObjectToString(date), getAllProducts, undefined, undefined, true);
            const withTaklit = !isSupplierNote && isTaklitEnabled()
                ? (await applyTaklitPricesToItems(ps, dateObjectToString(date), {onlyUntouched: true})).items
                : ps;
            setProducts(prev => {
                const prevById = new Map<number, IItem[]>();
                for (const p of prev) {
                    const hasQty = (p.quantity ?? 0) + (p.quantityReturns ?? 0) + (p.quantitySecond ?? 0) + (p.quantityThird ?? 0) > 0;
                    if (!hasQty && !p.priceTouched) continue;
                    const arr = prevById.get(p.id);
                    if (arr) arr.push(p); else prevById.set(p.id, [p]);
                }
                const result: IItem[] = [];
                for (const np of withTaklit) {
                    const existings = prevById.get(np.id);
                    if (!existings || existings.length === 0) {
                        result.push(np);
                        continue;
                    }
                    existings.forEach((ex, idx) => {
                        const base = idx === 0 ? np : { ...np };
                        const bothUnits = (ex.quantitySecond ?? 0) > 0 && (ex.quantity ?? 0) > 0;
                        const merged: IItem = {
                            ...base,
                            quantity: ex.quantity,
                            quantityReturns: ex.quantityReturns,
                            quantitySecond: ex.quantitySecond,
                            quantityThird: ex.quantityThird,
                            notePlacement: ex.notePlacement,
                            conversion_ratio: isVariableConversionRate()
                                ? (ex.conversion_ratio ?? base.conversion_ratio)
                                : (bothUnits ? 0 : base.conversion_ratio),
                        };
                        if (ex.priceTouched) {
                            merged.totalPrice = ex.totalPrice;
                            merged.priceTouched = true;
                        }
                        result.push(merged);
                    });
                    prevById.delete(np.id);
                }
                if (documentType === 'supplier_note') {
                    prevById.forEach((existings) => {
                        existings.forEach((ex) => result.push(ex));
                    });
                }
                return result;
            });
            if (drivers.length > 0) {
                const daily = await getClientsDaily((date), (date), Number(id ?? upid), undefined, undefined, undefined, undefined);
                if (daily?.length ?? 0 > 0) {
                    setDriver(drivers.find((s) => s.id == daily[0].driver_id))
                } else {
                    setDriver(undefined)
                }
            }
            setProductLoaded(true);
        };
        genProds();

    }, [date, getAllProducts]);
    let counter = 0;
    const onProductChange = React.useCallback((product: IItem) => {
        setProducts((prevProducts) => {
            // Prefer id + notePlacement; if product has no notePlacement (e.g. restored draft row)
            // fall back to matching the first row with the same id instead of appending a duplicate.
            let updated = false;
            applyDualUnitAmounts(product, isVariableConversionRate());
            const x = prevProducts.map((p) => {
                if (updated || product.id !== p.id) return p;
                if (product.notePlacement !== undefined) {
                    if (p.notePlacement === product.notePlacement) {
                        updated = true;
                        if (product.totalPrice !== p.totalPrice) product.priceTouched = true;
                        else if (p.priceTouched) product.priceTouched = true;
                        return product;
                    }
                    return p;
                }
                updated = true;
                product.notePlacement = p.notePlacement;
                if (product.totalPrice !== p.totalPrice) product.priceTouched = true;
                else if (p.priceTouched) product.priceTouched = true;
                return product;
            });
            if (!updated) {
                if (product.notePlacement == undefined) {
                    product.notePlacement = counter;
                    counter += 1;
                }
                x.push(product);
            }
            return x;
        });
    }, []);
    const onProductChangeOnce = React.useCallback((product: IItem) => {
        setProducts((prevProducts) => {
                let seen = false;
                applyDualUnitAmounts(product, isVariableConversionRate());
                const x = prevProducts.map((p) => {
                        if (seen || product.id !== p.id) return p;
                        // If the edited row has a notePlacement, update only the matching row.
                        // Otherwise update the first row with the same id (legacy behavior).
                        if (product.notePlacement !== undefined) {
                            if (p.notePlacement === product.notePlacement) {
                                seen = true;
                                if (product.totalPrice !== p.totalPrice) product.priceTouched = true;
                                else if (p.priceTouched) product.priceTouched = true;
                                return product;
                            }
                            return p;
                        }
                        seen = true;
                        product.notePlacement = p.notePlacement;
                        if (product.totalPrice !== p.totalPrice) product.priceTouched = true;
                        else if (p.priceTouched) product.priceTouched = true;
                        return product;
                    }
                );
                if (!seen) {
                    if (product.notePlacement == undefined) {
                        product.notePlacement = counter;
                        counter += 1;
                    }
                    x.push(product);
                }
                return x;

            }
        );
    }, []);

    const clearProducts = React.useCallback(() => {
        setProducts((prevProducts) => prevProducts.map((p) => ({...p, quantity: 0, quantityReturns: 0})));
    }, []);

    // Handler to open duplicate calendar and fetch delivery notes
    const onOpenDuplicateCalendar = React.useCallback(() => {
        // Set initial calendar range (e.g., last 3 months to next month)
        const startDate = new Date();
        startDate.setMonth(startDate.getMonth() - 3);
        const endDate = new Date();
        endDate.setMonth(endDate.getMonth() + 1);

        setCalendarRange([startDate, endDate]);
        setOpenDuplicateCalendar(true);
        setIsLoadingDeliveryNotes(true);

        // This will trigger the useEffect that calls refetchDeliveryNotes
    }, []);

    // Handler to duplicate products from selected delivery note
    const onDuplicateDeliveryNote = React.useCallback((selectedNote: ExtendedDocument) => {
        if (selectedNote && selectedNote.products) {
            // Copy products from the selected delivery note
            const duplicatedProducts = selectedNote.products.map((p, index) => ({
                ...p,
                notePlacement: index
            }));

            setProducts(duplicatedProducts);
            setOpenDuplicateCalendar(false);

            // Show success message
            Swal.fire({
                title: 'הצלחה!',
                text: `המוצרים מ${selectedNote.title} שוכפלו בהצלחה`,
                icon: 'success',
                timer: 2000,
                showConfirmButton: false
            });
        }
    }, []);

    React.useEffect(() => {
        const pr = products.filter((p) => p.quantity > 0 || p.quantityReturns > 0 || (p.quantitySecond > 0 && (p.conversion_ratio > 0 || documentType == "order")) || (documentType == "order" && (p.quantityThird ?? 0) > 0));

        if (pr.length === 0) {
            setMainValue(0);
            setTaxValue(0);
        }

        const result = JSDB().productDeliveryToAmount(NKTypeConvertor().toKotlinList(collect(pr, Number(id ?? upid), true, false)), Number(id ?? upid), dateObjectToString(date));
        setMainValue(JSDB().round(result.third, 2));
        setTaxValue(JSDB().round(result.second, 2));

    }, [products]);

    const createCustomerDocument = useCreateCustomerDocument(id ?? upid);

    // Draft key builder: clientId_docType
    const draftKey = React.useMemo(() => {
        const clientId = (id ?? upid) as string;
        return `draft_${clientId}_${documentType}`;
    }, [id, upid, documentType]);

    // Load draft on mount if exists and no productsFromAbove
    React.useEffect(() => {
        if (productsFromAbove) return;
        if(!productLoaded) return;
        try {
            const raw = localStorage.getItem(draftKey);
            if (!raw) return;
            Swal.fire({
                title: 'שחזור טיוטה שמורה?',
                text: 'נמצאה טיוטה שמורה עבור לקוח ומסמך זה. האם לשחזר אותה?',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'שחזר',
                cancelButtonText: 'מחק'
            }).then((res) => {
                if (res.isConfirmed) {
                    try {
                        const parsed = JSON.parse(raw);
                        if (!parsed) return;
                        if (parsed.products?.length) {
                            const min = parsed.products as {id:number, quantity:number, quantityReturns:number, quantitySecond:number, quantityThird?:number, notePlacement?:number, totalPrice?:number, priceTouched?:boolean, conversion_ratio?:number}[];


                            setProducts(prev => {
                                const draftById = new Map<number, typeof min>();
                                for (const m of min) {
                                    const arr = draftById.get(m.id);
                                    if (arr) arr.push(m); else draftById.set(m.id, [m]);
                                }
                                const result: IItem[] = [];
                                for (const p of prev) {
                                    const matches = draftById.get(p.id);
                                    if (!matches || matches.length === 0) {
                                        result.push(p);
                                        continue;
                                    }
                                    matches.forEach((m, idx) => {
                                        const bothUnits = (m.quantitySecond ?? 0) > 0 && (m.quantity ?? 0) > 0;
                                        const row: IItem = {
                                            ...p,
                                            quantity: m.quantity ?? 0,
                                            quantityReturns: m.quantityReturns ?? 0,
                                            quantitySecond: m.quantitySecond ?? 0,
                                            quantityThird: m.quantityThird ?? 0,
                                            notePlacement: m.notePlacement ?? (idx === 0 ? p.notePlacement : undefined),
                                            conversion_ratio: isVariableConversionRate()
                                                ? (m.conversion_ratio ?? p.conversion_ratio)
                                                : (bothUnits ? 0 : (m.conversion_ratio ?? p.conversion_ratio)),
                                        };
                                        if (m.priceTouched && m.totalPrice !== undefined) {
                                            row.totalPrice = m.totalPrice;
                                            row.priceTouched = true;
                                        }
                                        result.push(row);
                                    });
                                    draftById.delete(p.id);
                                }
                                return result;
                            });
                        }
                        if (parsed.commentControl) setCommentControl(parsed.commentControl);
                        if (parsed.autoNum !== undefined) setAutoNum(parsed.autoNum);
                        if (parsed.bul !== undefined) setBul(parsed.bul);
                        if (parsed.savePrice !== undefined) setSavePrice(parsed.savePrice);
                    } catch {}
                } else {
                    try { localStorage.removeItem(draftKey); } catch {}
                }
            });
        } catch (e) {
            // ignore
        }
    }, [productLoaded]);

    // Save draft whenever relevant state changes and creation not yet succeeded
    React.useEffect(() => {
        if (createCustomerDocument.isSuccess) return; // do not overwrite after success
        if(!productLoaded) return;

        const draft = {
            products: products.map(p => ({
                id: p.id,
                quantity: p.quantity ?? 0,
                quantityReturns: p.quantityReturns ?? 0,
                quantitySecond: p.quantitySecond ?? 0,
                quantityThird: p.quantityThird ?? 0,
                notePlacement: p.notePlacement,
                totalPrice: p.totalPrice,
                priceTouched: p.priceTouched === true,
                conversion_ratio: p.conversion_ratio,
            })).filter((p)=>p.quantity+p.quantityReturns+p.quantitySecond+(p.quantityThird??0)>0 || p.priceTouched),
            commentControl,
            autoNum,
            bul,
            savePrice
        };
        try {

            localStorage.setItem(draftKey, JSON.stringify(draft));
        } catch (e) {
            console.log(e)
            // ignore quota errors
        }
    }, [products, commentControl, autoNum, bul, savePrice, draftKey, createCustomerDocument.isSuccess]);

    // When document created successfully, delete draft
    React.useEffect(() => {
        if (createCustomerDocument.isSuccess) {
            try { localStorage.removeItem(draftKey); } catch (e) {}
        }
    }, [createCustomerDocument.isSuccess, draftKey]);

    const onProductChangeReal = (createCustomerDocument.data) ? undefined : onProductChange;
    React.useEffect(() => {
        if((markFromOrder || fromOrder) && createCustomerDocument.data){
            // fromOrder(createCustomerDocument.data)
        }
    }, [createCustomerDocument.data, markFromOrder, fromOrder]);

    if (!customer || (!id && !upid)) return <Box/>;

    return (
        <MainCard
            title={
                <Typography variant='h3'>
                    {documentType == 'delivery_certificate' ?
                        <FormattedMessage id='Create new delivery certificate'/> : 'הזמנה חדשה'}
                </Typography>
            }
        >
            <Grid container spacing={3}>
                {createCustomerDocument.data === undefined && !narrow ?
                    <Grid item xs={7}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <DocumentCustomerDetails customer={customer} date={date} setDate={setDate}
                                                         driver={driver} drivers={drivers} setDriver={setDriver}
                                                         setDetails={setDetails} details={details}/>
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <Typography variant='h4'>
                                        פרטים נוספים
                                    </Typography>
                                    <Tooltip title='פרטים מאולצים למסמך (כתובת / שם עסק)'>
                                        <IconButton
                                            size='small'
                                            color={forceStatic ? 'primary' : 'default'}
                                            onClick={() => setOpenForce(true)}
                                        >
                                            <PersonIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ContentCopyIcon />}
                                        onClick={onOpenDuplicateCalendar}
                                        disabled={isLoadingDeliveryNotes}
                                    >
                                       שכפל תעודה
                                    </Button>
                                    <Typography variant="caption" color="textSecondary">
                                        בחר {documentType === 'delivery_certificate' ? 'תעודת משלוח' : 'הזמנה'} קיימ{documentType === 'delivery_certificate' ? 'ת' : 'ה'} כדי לשכפל את המוצרים
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={6} sm={6}>
                                <TextField
                                    fullWidth
                                    id='comment'
                                    name='comment'
                                    label='הערות'
                                    value={commentControl.notes}
                                    onChange={(e) => {
                                        setCommentControl({...commentControl, notes: e.target.value});
                                    }}
                                />
                            </Grid>
                            <Grid item xs={3} sm={3}>
                                <TextField
                                    fullWidth
                                    id='bul'
                                    name='bul'
                                    label='מספר בול'
                                    type='number'
                                    onChange={(v) => {
                                        setBul(Math.floor(Number(v.target.value)));
                                    }}
                                    value={bul}
                                    onKeyDown={(e) => {
                                        if (e.keyCode === 38 || e.keyCode === 40) {
                                            e.preventDefault();
                                        }
                                    }
                                    }
                                    onWheel={(e) => (e.target as HTMLElement).blur()}
                                />
                            </Grid>

                            <Grid item xs={3} sm={3}>
                                <NoNegative
                                    fullWidth
                                    id='autoNum'
                                    name='autoNum'
                                    label='מספור ידני'
                                    changer={(v) => {
                                        if (v != 0)
                                            setAutoNum((v));
                                        else
                                            setAutoNum(undefined);
                                    }
                                    }
                                    value={autoNum}


                                />
                            </Grid>



                            <Grid item xs={12} sm={12} md={12} alignItems='center'>
                                <CustomerProductsList products={products} onProductChange={onProductChangeOnce}
                                                      clearFunc={clearProducts}
                                                      withReturns={documentType == 'delivery_certificate' || documentType == "order" || documentType == "supplier_note" || documentType == "tax_invoice" || documentType == "invoice" }
                                                      getAllProducts={getAllProducts}
                                                      setGetAllProducts={setGetAllProducts}
                                                      sup={documentType=='supplier_note'}
                                                      supplierId={documentType=='supplier_note' ? (id ?? upid) : undefined}
                                                      documentDate={date}

                                />
                            </Grid>
                        </Grid>
                    </Grid> : undefined
                }
                <Grid item xs={narrow ? 12 : 5}>
                    <Grid container>
                        <Grid item xs={12} sm={12}>
                            <DocumentPreview
                                outerDoc={createCustomerDocument.data ? undefined : setCommentControl}
                                document={
                                    {
                                        entId: customer.id,
                                        date: date ?? new Date(),
                                        type: documentType,
                                        entName: customer.name,
                                        products:
                                            ((createCustomerDocument.data) ?
                                                ((documentType == 'delivery_certificate' || documentType == "tax_invoice" || documentType == "invoice" || documentType == "refund") ?
                                                    (createCustomerDocument.data as ProductNote[])[0].products : (createCustomerDocument.data as ProductNote).products) : products)
                                                .filter((p) => p.quantity > 0 || p.quantityReturns > 0 || p.forceShow || (p.quantitySecond > 0 && (p.conversion_ratio > 0 || documentType == "order")) || (documentType == "order" && (p.quantityThird ?? 0) > 0)),
                                        status: ((createCustomerDocument.data) ?
                                            ((documentType == 'delivery_certificate' || documentType == "tax_invoice" || documentType == "invoice" || documentType == "refund") ?
                                                (createCustomerDocument.data as ProductNote[])[0].status : (createCustomerDocument.data as ProductNote).status) : 'pending_approval'),
                                        id: ((createCustomerDocument.data) ?
                                            ((documentType == 'delivery_certificate' || documentType == "tax_invoice" || documentType == "invoice" || documentType == "refund") ?
                                                (createCustomerDocument.data as ProductNote[])[0].id : (createCustomerDocument.data as ProductNote).id) : autoNum ?? 0),
                                        end: new Date(),
                                        color: '',
                                        textColor: '',
                                        baseColor: '',
                                        description: '',
                                        title: ((createCustomerDocument.data) ?
                                            ((documentType == 'delivery_certificate' || documentType == "tax_invoice" || documentType == "invoice" || documentType == "refund") ?
                                                (createCustomerDocument.data as ProductNote[])[0].title : (createCustomerDocument.data as ProductNote).title) : 'תעודה חדשה'),
                                        allDay: false,
                                        nkObject: ((createCustomerDocument.data) ?
                                            ((documentType == 'delivery_certificate' || documentType == "tax_invoice" || documentType == "invoice" || documentType == "refund") ?
                                                (createCustomerDocument.data as ProductNote[])[0].nkObject : (createCustomerDocument.data as ProductNote).nkObject) : undefined),
                                        onProductChange: onProductChangeReal,
                                        onDateChange: (createCustomerDocument.data) ? undefined : setDate,
                                        onIdChange: (createCustomerDocument.data) ? undefined : setAutoNum,
                                        agent: JSDB().currentAgent,
                                        issueDate: new Date(),
                                        name: '',
                                        mainValue: mainValue,
                                        taxValue: tax,
                                        comment: commentControl.notes,
                                        notes2: commentControl.notes2,
                                        notes3: commentControl.notes3,
                                        notes4: commentControl.notes4,
                                        notes5: commentControl.notes5,
                                    bul: bul,
                                    staticData: forceStatic ? forcedDetails : undefined,
                                    // Pass handler to update products from loader
                                    onProductsLoaded: setProducts,
                                    userProducts: products
                                }}
                            />
                        </Grid>
                        {withDiscount && documentType === 'delivery_certificate' && (
                            <Grid item xs={12} sm={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={!!savePrice}
                                            onChange={(e) => setSavePrice(e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label={'שמירת מחיר רק בתעודה זו'}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12} sm={12}>
                            <Box
                                sx={{display: 'flex', flex: 1, flexDirection: 'row', justifyContent: 'space-evenly'}}>
                                <LoadingButton
                                    onClick={() => {
                                        // Orders keep second-unit-only lines; only issued documents drop them.
                                        if (documentType !== 'order' && products.some(needsTypedFirstUnit)) {
                                            Swal.fire({
                                                title: 'לא מילאת את כל הנתונים',
                                                text: TYPED_FIRST_UNIT_MESSAGE,
                                                icon: 'warning',
                                            });
                                            return;
                                        }
                                        const newDoc = createDocument({
                                            mainValue: 0,
                                            date: date ?? new Date(),
                                            type: documentType,
                                            products: products.filter((p) => p.quantity > 0 || p.quantityReturns > 0 || (p.quantitySecond > 0 && (p.conversion_ratio > 0 || documentType == "order")) || (documentType == "order" && (p.quantityThird ?? 0) > 0)),
                                            entId: id,
                                            id: autoNum,
                                            status: 'approved',
                                            comment: commentControl.notes,
                                            notes2: commentControl.notes2,
                                            notes3: commentControl.notes3,
                                            notes4: commentControl.notes4,
                                            notes5: commentControl.notes5,
                                            bul,
                                            staticData: forceStatic ? forcedDetails : undefined
                                        })
                                        if (refId && refId.length > 0 && productsFromAbove)
                                            newDoc.refId = refId[0]
                                        if(markFromOrder || fromOrder)
                                            newDoc.fromOrder = true
                                        if(savePrice)
                                            newDoc.savePrice = savePrice
                                        narrow ? createCustomerDocument.mutate(
                                                newDoc
                                            ) :
                                            Swal.fire({
                                                title: 'האם תרצה להנפיק מסמך?',
                                                icon: 'warning',
                                                showCancelButton: true,
                                                confirmButtonColor: theme.palette.primary.dark,
                                                cancelButtonColor: theme.palette.error.light,
                                                confirmButtonText: 'הנפק',
                                                cancelButtonText: 'בטל',
                                                allowEnterKey: true,
                                                allowEscapeKey: true,
                                                keydownListenerCapture: true,
                                                focusConfirm: true
                                            }).then((result) => {
                                                if (result.isConfirmed) {

                                                    createCustomerDocument.mutate(
                                                        newDoc
                                                    );
                                                }
                                            })
                                    }

                                    }
                                    loading={createCustomerDocument.isLoading}
                                    disabled={createCustomerDocument.isSuccess || products.filter((p) => p.quantity > 0 || p.quantityReturns > 0 || (p.quantitySecond > 0 && (p.conversion_ratio > 0 || documentType == "order")) || (documentType == "order" && (p.quantityThird ?? 0) > 0)).length === 0}
                                    variant='contained'
                                    type='submit'
                                    fullWidth
                                    sx={{m: 2}}
                                >
                                    הנפק
                                </LoadingButton>
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Dialog for duplicating delivery notes */}
            <Dialog
                open={openDuplicateCalendar}
                onClose={() => setOpenDuplicateCalendar(false)}
                fullWidth
                maxWidth='lg'
                sx={{'& .MuiDialog-paper': {p: 0}}}
            >
                <CalendarContainer
                    setOpenCalendar={setOpenDuplicateCalendar}
                    events={deliveryNotesForDuplicate ?? []}
                    receipts={[]}
                    onAddDeliveryCertificate={onDuplicateDeliveryNote}
                    onRemoveDeliveryCertificate={() => {}}
                    setPreviewOpen={setPreviewOpen}
                    setPreviewData={setPreviewData}
                    setRange={setCalendarRange}
                />
            </Dialog>

            {/* Preview dialog */}
            <Dialog
                fullWidth
                open={previewOpen}
                onBackdropClick={() => setPreviewOpen(false)}
                sx={{'& .MuiDialog-paper': {p: 0}}}
            >
                <DocumentPreview document={previewData} />
            </Dialog>

            {/* Forced (static_data) fields dialog */}
            <Dialog open={openForce} onClose={() => setOpenForce(false)} fullWidth maxWidth='sm'>
                <Box sx={{p: 3}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                        <Typography variant='h4'>פרטים מאולצים למסמך</Typography>
                        <FormControlLabel
                            control={<Checkbox checked={forceStatic} onChange={(e) => setForceStatic(e.target.checked)} color='primary'/>}
                            label='אלץ פרטים'
                        />
                    </Box>
                    <Typography variant='caption' color='textSecondary'>
                        שם הלקוח נשאר כפי שמוגדר בכרטיס; ניתן לאלץ כתובת ושם עסק/ח.פ/טלפון למסמך זה בלבד.
                    </Typography>
                    <Grid container spacing={2} sx={{mt: 0.5}}>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label='שם עסק' value={forcedDetails.companyName}
                                       onChange={(e) => setForcedDetails({...forcedDetails, companyName: e.target.value})}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label='ח.פ / עוסק' value={forcedDetails.companyId}
                                       onChange={(e) => setForcedDetails({...forcedDetails, companyId: e.target.value})}/>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label='עיר' value={forcedDetails.city}
                                       onChange={(e) => setForcedDetails({...forcedDetails, city: e.target.value})}/>
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField fullWidth label='רחוב' value={forcedDetails.street}
                                       onChange={(e) => setForcedDetails({...forcedDetails, street: e.target.value})}/>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField fullWidth label='מספר' value={forcedDetails.streetNumber}
                                       onChange={(e) => setForcedDetails({...forcedDetails, streetNumber: e.target.value})}/>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label='טלפון' value={forcedDetails.phone}
                                       onChange={(e) => setForcedDetails({...forcedDetails, phone: e.target.value})}/>
                        </Grid>
                    </Grid>
                    <Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 2}}>
                        <Button variant='contained' onClick={() => { setForceStatic(true); setOpenForce(false); }}>
                            שמור
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </MainCard>
    );
};
export default memo(NewDeliveryCertificate);
